// src/App.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import all necessary page components
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import StakeSelectionPage from './components/StakeSelectionPage';
import BingoGamePage from './components/BingoGamePage';
import CartellaConfirmationPage from './components/CartellaPage';
import PlayCartellaPage from './components/PlayCartellaPage';
import DashboardPage from './components/DashboardPage';
import WalletPage from './components/WalletPage';
import ProfilePage from './components/ProfilePage';
import HistoryPage from './components/HistoryPage';
import LeaderboardPage from './components/LeaderboardPage';
import HowToPlayPage from './components/HowToPlayPage';
import ReferralIncomePage from './components/ReferralIncomePage';
import MoneyBagGamePage from './components/MoneyBagGamePage'; // Re-enabled
import DiamondGamePage from './components/DiamondGamePage'; // Re-enabled
import TelegramLoginPage from './components/TelegramLoginPage';
import FastKenoGamePage from './components/FastKenoGamePage';
// Import FloatingContactButton for global floating chat button
import FloatingContactButton from './components/FloatingContactButton';

// Common components and styling
import SharedHeader from './common/SharedHeader';
import './App.scss'; // Global application styling

// Contexts
import { UserProvider, UserContext } from './contexts/UserContext';
import { LanguageProvider, LanguageContext } from './contexts/LanguageContext';
import { GlobalModalProvider, useGlobalModal } from './contexts/GlobalModalContext';
//import { getAdvertisingContent } from './common/advertisingContent';

// PrivateRoute component to protect routes
const PrivateRoute = ({ children, setCurrentPage, isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null, true, or false

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        navigate('/login', { state: { from: location.pathname } });
      }
    };
    checkAuth();
  }, [navigate, location]);

  if (isAuthenticated === null) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-900 text-white">
        Loading authentication...
      </div>
    );
  }

  return isAuthenticated ? (
    <>
      <SharedHeader isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
      {children}
    </>
  ) : null;
};

// Simple Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-red-900 text-white p-4">
          <h1 className="text-3xl font-bold mb-4">Something went wrong.</h1>
          <p className="text-lg mb-4">We're sorry for the inconvenience. Please try again later.</p>
          {process.env.NODE_ENV === 'development' && (
            <details className="whitespace-pre-wrap text-sm text-red-200">
              {this.state.error && this.state.error.toString()}
              <br />
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            className="mt-6 bg-white text-red-900 font-bold py-2 px-4 rounded-lg shadow-md hover:bg-gray-200"
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// New component to handle modal display on route changes
const AppContent = () => {
  const location = useLocation();
  const { showModal } = useGlobalModal();
  const { language } = useContext(LanguageContext);
  const { userData } = useContext(UserContext); // get full userData from UserContext

  // Extract userId safely (ignore "N/A" or undefined)
  const userId = userData?.id && userData.id !== "N/A" ? userData.id : null;
  {/** 
  useEffect(() => {
    if (location.pathname === '/dashboard' && !sessionStorage.getItem('dashboardModalShown')) {
      const { content, props } = getAdvertisingContent(language);
      showModal(content, props);
      sessionStorage.setItem('dashboardModalShown', 'true');
    }
  }, [location.pathname, language, showModal]);
*/}
  const [isSidebarOpenState, setIsSidebarOpenState] = useState(false);
  const [currentPageState, setCurrentPageState] = useState('');

  // Memoize state setters using useCallback
  const setIsSidebarOpen = useCallback((isOpen) => {
    setIsSidebarOpenState(isOpen);
  }, []);

  const setCurrentPage = useCallback((pageName) => {
    setCurrentPageState(pageName);
  }, []);


  const privateRoutesMap = {
    "/dashboard": DashboardPage,
    "/stake-selection": StakeSelectionPage,
    "/fast-keno": FastKenoGamePage,
    "/bingo-game": BingoGamePage,
    "/cartella": CartellaConfirmationPage,
    "/play-cartella": PlayCartellaPage,
    "/money-bag-game": MoneyBagGamePage, // Re-enabled
    "/diamond-game": DiamondGamePage, // Re-enabled
    "/wallet": WalletPage,
    "/profile": ProfilePage,
    "/history": HistoryPage,
    "/leaderboard": LeaderboardPage,
    "/how-to-play": HowToPlayPage,
    "/referral-income": ReferralIncomePage,
  };

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage setCurrentPage={setCurrentPage} />} />
        <Route path="/signup" element={<SignupPage setCurrentPage={setCurrentPage} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage setCurrentPage={setCurrentPage} />} />
        <Route path="/telegram-login" element={<TelegramLoginPage />} />

        {/* Private Routes */}
        {Object.entries(privateRoutesMap).map(([path, Component]) => (
          <Route
            key={path}
            path={path}
            element={
              <PrivateRoute
                isSidebarOpen={isSidebarOpenState}
                setIsSidebarOpen={setIsSidebarOpen}
                setCurrentPage={setCurrentPage}
              >
                <Component
                  isSidebarOpen={isSidebarOpenState}
                  setIsSidebarOpen={setIsSidebarOpen}
                  setCurrentPage={setCurrentPage}
                />
              </PrivateRoute>
            }
          />
        ))}

        <Route path="*" element={<LoginPage setCurrentPage={setCurrentPage} />} />
      </Routes>

      {/* Show floating contact button only if userId is valid */}
      {userId && <FloatingContactButton userId={userId} lang={language} />}
    </ErrorBoundary>
  );
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <UserProvider>
          <GlobalModalProvider>
            <AppContent />
          </GlobalModalProvider>

          <ToastContainer position="top-right" autoClose={3000} />
        </UserProvider>
      </Router>
    </LanguageProvider>
  );
}

export default App;
